// Shared loader for straight knives (M9 bayonet, skeleton knife): a model made by one of the import
// scripts, with a part named "blade" and the rest (grip, guard, handle...) in plain colours. The blade
// takes the game's finishes; everything else keeps its colour.
//
// The knife is placed in the knife arm's coordinates like the butterfly (butterflyModel.ts): +X runs
// along the knife from the index finger toward the pommel, the blade out on the thumb side. The axis
// comes from the parts (blade middle -> other parts' middle) and the blade's flat side from where its
// points spread the most, so a model exported at any angle lies right in the hand.

import * as THREE from 'three';
import type { KnifeModel } from './karambitModel';
import type { KnifeGrip } from './fpArms';
import { projectBladeUvs } from './bladeUvs';

export interface StraightKnifeSpec {
  url: string;
  /** Colours of the parts other than the blade. */
  colors: Record<string, number>;
  /** Model units -> knife arm units (only the proportions matter). */
  fitScale: number;
  /**
   * The index finger: on the knife's axis, \`offset\` model units toward the pommel from \`part\`'s
   * middle, or from its blade-side end.
   */
  index: { part: string; from: 'middle' | 'bladeEnd'; offset: number };
  /** How much of the knife the fist covers, from the index finger (model units). */
  fistLength: number;
  /** Turns the cutting edge (or the spine) toward the knuckles (+1) or the other way (-1). */
  edgeSide: 1 | -1;
  /** Its own inspect pose (fpArms.ts KnifeGrip). */
  inspect?: KnifeGrip['inspect'];
}

/** Load a straight knife once per spec. `blade` is the finish material (shared by all knives). */
export function straightKnifeLoader(spec: StraightKnifeSpec) {
  let loading: Promise<KnifeModel | null> | null = null;
  return (blade: THREE.Material): Promise<KnifeModel | null> => {
    loading ??= load(spec, blade).catch(() => null);
    return loading;
  };
}

async function load(spec: StraightKnifeSpec, blade: THREE.Material): Promise<KnifeModel | null> {
  const [{ GLTFLoader }, { toCreasedNormals }] = await Promise.all([
    import('three/addons/loaders/GLTFLoader.js'),
    import('three/addons/utils/BufferGeometryUtils.js'),
  ]);
  const gltf = await new GLTFLoader().loadAsync(spec.url);
  gltf.scene.updateMatrixWorld(true);
  const parts = new Map<string, THREE.BufferGeometry>();
  gltf.scene.traverse((o) => {
    if (o instanceof THREE.Mesh)
      parts.set(o.name.replace(/_\d+$/, ''), o.geometry.clone().applyMatrix4(o.matrixWorld));
  });
  const bladeGeo = parts.get('blade');
  if (!bladeGeo || parts.size < 2) return null;

  const box = (g: THREE.BufferGeometry) => {
    g.computeBoundingBox();
    return g.boundingBox!;
  };
  // Knife axis: from the middle of the blade to the middle of everything else (blade -> pommel = +X).
  const bladeC = box(bladeGeo).getCenter(new THREE.Vector3());
  const rest = new THREE.Box3();
  for (const [name, g] of parts) if (name !== 'blade') rest.union(box(g));
  const ax = rest.getCenter(new THREE.Vector3()).sub(bladeC).normalize();

  // The blade's flat side: across it, the direction in which the blade is widest (its points spread
  // the most), at right angles to the axis.
  const pos = bladeGeo.getAttribute('position');
  const p = new THREE.Vector3();
  const u = new THREE.Vector3(1, 0, 0);
  if (Math.abs(u.dot(ax)) > 0.9) u.set(0, 1, 0);
  u.addScaledVector(ax, -u.dot(ax)).normalize();
  const v = new THREE.Vector3().crossVectors(ax, u);
  let cuu = 0;
  let cuv = 0;
  let cvv = 0;
  for (let i = 0; i < pos.count; i++) {
    p.fromBufferAttribute(pos, i).sub(bladeC);
    const a = p.dot(u);
    const b = p.dot(v);
    cuu += a * a;
    cuv += a * b;
    cvv += b * b;
  }
  const t = 0.5 * Math.atan2(2 * cuv, cuu - cvv);
  const ay = u
    .clone()
    .multiplyScalar(Math.cos(t))
    .addScaledVector(v, Math.sin(t))
    .normalize()
    .multiplyScalar(spec.edgeSide);
  const az = new THREE.Vector3().crossVectors(ax, ay);

  // The index finger, on the knife's axis.
  const at = parts.get(spec.index.part);
  if (!at) return null;
  let along: number;
  if (spec.index.from === 'middle') along = box(at).getCenter(p).sub(bladeC).dot(ax);
  else {
    along = Infinity;
    const ip = at.getAttribute('position');
    for (let i = 0; i < ip.count; i++)
      along = Math.min(along, p.fromBufferAttribute(ip, i).sub(bladeC).dot(ax));
  }
  const index = bladeC.clone().addScaledVector(ax, along + spec.index.offset);

  projectBladeUvs(bladeGeo, index, ax.clone().negate(), ay);
  const inner = new THREE.Group();
  for (const [name, geo] of parts) {
    const mat =
      name === 'blade' ? blade : new THREE.MeshPhongMaterial({ color: spec.colors[name] ?? 0x0e0f12 });
    const mesh = new THREE.Mesh(toCreasedNormals(geo, (35 * Math.PI) / 180), mat);
    mesh.name = name;
    mesh.frustumCulled = false;
    inner.add(mesh);
  }
  const s = spec.fitScale;
  const basis = new THREE.Matrix4().makeBasis(ax, ay, az).transpose();
  inner.applyMatrix4(
    new THREE.Matrix4()
      .makeScale(s, s, s)
      .multiply(basis)
      .multiply(new THREE.Matrix4().makeTranslation(-index.x, -index.y, -index.z)),
  );
  const object = new THREE.Group();
  object.add(inner);
  const grip: KnifeGrip = {
    index: new THREE.Vector3(0, 0, 0),
    handle: spec.fistLength * s,
    inspect: spec.inspect,
  };
  object.userData.grip = grip;
  // No inserts to colour: these knives are black with the finish on the blade.
  return { object, setAccent: () => {} };
}
